import { Visualizer, VisualizerControl, VisualizationUpdateLevel } from '../data-source/visualizer';
import { Drawer } from '../panel/cabinets';
import { getPaletteColor, colorToString, adjustColor } from '../util/color';
import { Timeline } from '../timeline/timeline';
import { LocationTime } from '../data-source/types';
import { LocationTimeMonitor } from '../application/location-time-monitor';
import { Map } from '../map/map';
import { ModelingDataSource } from './modeling';
import { ModelingContainer, ModelingVisualizerControl } from './container-modeling';
import { GoiDatum } from '../poi/poi-common';
import { getCategoriesLegend, getCategoryColor, getImpactScoreColor, getImpactScoreLegend, getScalarFieldLegend, ModelProject } from './modeling-common';
import { Subscription } from 'rxjs';
import { getDateTimeFormat } from '../application/i18n';
import { replaceNodeContent } from '../dom/dom';
import { OverlayDistribution, CombineMode, ColorMode } from '../worker/scalar-field-manager';
import * as CryptoJS from 'crypto-js';
import { CacheEntry } from '../worker/scalar-field-worker';
import { GoiImpact, BandImpact } from '../scalar-field/scalar-field';
import * as htl from 'htl';
import { Color } from '../svg/svg';

export class ModelingInfoVisualizer implements Visualizer {
	private inspectionContainer: ModelingContainer
	private source: ModelingDataSource;
	private map: Map;
	private timeline: Timeline;
	private handleLocationTimeUpdate: (locationTime: LocationTime) => void;
	private locationTimeMonitor: LocationTimeMonitor;
	private scalarsSubscription: Subscription;

	private cursorTimeContainer: HTMLSpanElement;
	private dateTimeFormat: Intl.DateTimeFormat;

	private sfAllLegendContainer: HTMLDivElement;
	private sfCategoryLegendContainer: HTMLDivElement;
	private impactLegendContainer: HTMLDivElement;
	private categoryLegendContainer: HTMLDivElement;

	private cloneContainer: HTMLDivElement;
	private projectsContainer: HTMLDivElement;

	//holds the impacts of all projects combined
	private projectDetailsAllContainer: HTMLDivElement;
	private impactsAllContainer: HTMLDivElement;
	private activeCacheEntry: CacheEntry;

	public constructor(private visualizerKey: string, private mdlType: string, private drawer: Drawer) {
		this.inspectionContainer = <ModelingContainer>this.drawer.getContainer();
		this.source = this.inspectionContainer.getDataSource();

		let infoSectionContainer = <HTMLDivElement>drawer.getContent().querySelector('div.tlmd-sections > div.tlmd-content.info');
		
		this.sfAllLegendContainer = <HTMLDivElement>infoSectionContainer.querySelector('div.tlmd-legend div.tlmd-legend-group.sf-all');
		this.sfCategoryLegendContainer = <HTMLDivElement>infoSectionContainer.querySelector('div.tlmd-legend div.tlmd-legend-group.sf-category');
		this.impactLegendContainer = <HTMLDivElement>infoSectionContainer.querySelector('div.tlmd-legend div.tlmd-legend-group.impact');
		this.categoryLegendContainer = <HTMLDivElement>infoSectionContainer.querySelector('div.tlmd-legend div.tlmd-legend-group.category');

		this.projectDetailsAllContainer = <HTMLDivElement>infoSectionContainer.querySelector('div.tlmd-project-details.all');

		this.impactsAllContainer = <HTMLDivElement>this.projectDetailsAllContainer.querySelector('div.tlmd-project-details.all>div.tlmd-foldout');

		this.cursorTimeContainer = <HTMLSpanElement>this.projectDetailsAllContainer.querySelector('span.tlmd-project-detail-cells>span.time');
		this.dateTimeFormat = getDateTimeFormat({ year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' });

		this.cloneContainer = <HTMLDivElement>infoSectionContainer.querySelector('div.tlmd-skeleton');
		this.projectsContainer = <HTMLDivElement>infoSectionContainer.querySelector('div.tlmd-projects');


		//the this keyword will be set to the scalars observable. We need access to this.source hence this construct
		let self = this;
		this.handleLocationTimeUpdate = (locationTime: LocationTime): void => {
			self.source.getScalarFieldManager(this.mdlType).requestScalars(locationTime);
		}

		this.locationTimeMonitor = new LocationTimeMonitor();
		this.scalarsSubscription = this.source.getScalarFieldManager(this.mdlType).getScalarsObservable().subscribe(scalarMap => {
			if (scalarMap) {
				let catecoryScalars = Array.from(this.source.getData()[this.type()].getCategorizedModelProjects().entries()).reduce((map, entry) => {
					map[entry[0]] =
						entry[1]
							.map(project => scalarMap.scalars[project.id])
							.reduce((sum, scalar) => scalar != undefined && scalar > 0 ? (sum != undefined ? sum : 0) + scalar : sum, <number>undefined);
					return map;
				}, <{ [key: string]: number }>{});
				let allScalar = Object.values(scalarMap.scalars)
					.reduce((sum, scalar) => scalar != undefined && scalar > 0 ? (sum != undefined ? sum : 0) + scalar : sum, <number>undefined);

				//upate all scalar value displays

				let projectDetailsContainers = infoSectionContainer.querySelectorAll('div.tlmd-project-details');
				projectDetailsContainers.forEach((projectDetailContainer: HTMLDivElement) => {
					let value = projectDetailContainer.classList.contains('all')
						? allScalar
						: projectDetailContainer.classList.contains('category')
							? catecoryScalars[projectDetailContainer.dataset.category]
							: scalarMap.scalars[projectDetailContainer.dataset.project];
					(<HTMLSpanElement>projectDetailContainer.firstElementChild.firstElementChild.querySelector('span.scalar')).innerText = value == undefined ? '-' : value.toFixed(1);
				});


				//console.log(`scalars { lat: ${scalarMap.locationTime.location.lat}, lng: ${scalarMap.locationTime.location.lng}, time: ${scalarMap.locationTime.time},\n all: %s,\n categoryScalars: %o,\n scalars: %o }`, allScalar, catecoryScalars, scalarMap.scalars);
			}
		});

		this.activeCacheEntry = undefined;

		this.source.getScalarFieldManager(this.mdlType).setImpactConsumer((cacheEntry: CacheEntry) => {
			this.activeCacheEntry = cacheEntry;
			self.refresh(VisualizationUpdateLevel.data);
		});

	}


	public updateSelection(): void {
		// this visualizer cannot select data (yet)
	}

	public refresh(level: VisualizationUpdateLevel): void {
		let dataList = this.source.getData()[this.type()];
		let modelProjects = <ModelProject[]>dataList.getData();

		if (level <= VisualizationUpdateLevel.animation) {

			this.cursorTimeContainer.innerText = this.dateTimeFormat.format(this.timeline.getCursorMillis().center);

			if (level <= VisualizationUpdateLevel.filter) {
				this.handleLocationTimeUpdate(this.locationTimeMonitor.get());
			}
		}
		
		const grid = this.source.getScalarFieldManager(this.mdlType).getGrid()
		//check if the 'all' impact section has been setup and still inline with the project type settings
		let thresholds = modelProjects.length > 0 ? modelProjects[0].parameters.render.thresholds : undefined;
		let gois = this.inspectionContainer.getImpactMonitors(grid);
		let goiMap = this.inspectionContainer.getImpactMonitorsMap(grid);

		let impactHash = this.getImpactHash(thresholds, gois);

		let updateImpactOverview = this.impactsAllContainer.childElementCount == 0 || (<HTMLDivElement>this.impactsAllContainer.children[0]).dataset.hash != impactHash;
		let impactOverviewNode: HTMLDivElement = undefined;

		if (updateImpactOverview) {
			//the geometries of interest (impact monitors) and or the defined thresholds do not match the current impcat overview markup
			impactOverviewNode = impactHash ? this.createImpactOverview(thresholds, 'all', gois) : undefined;
			replaceNodeContent(this.impactsAllContainer, impactOverviewNode);
		} else {
			impactOverviewNode = <HTMLDivElement>this.impactsAllContainer.children[0];
		}

		this.setImpacts(this.projectDetailsAllContainer, this.source.getScalarFieldManager(this.mdlType).getModes().layout === CombineMode.all, 'all', thresholds, goiMap);

		//check/setup categories
		let categoryMap = dataList.getCategorizedModelProjects();
		let categoryEntries = this.projectsContainer.querySelectorAll('div.tlmd-model-category-info');
		

		let overlayDistributions = this.source.getScalarFieldManager(this.mdlType).getOverlayDistributions(modelProjects, categoryMap);


		//update/remove category sections
		categoryEntries.forEach((categoryNode: HTMLDivElement) => {

			if (categoryMap.get(categoryNode.dataset.category) === undefined || categoryMap.get(categoryNode.dataset.category).length === 0) {
				//remove this category because it is not among the set of existing projects
				categoryNode.parentNode.removeChild(categoryNode);
			} else {
				//update category with current set of model projects
				let categoryModelProjects = categoryMap.get(categoryNode.dataset.category);
				this.updateCategoryNode(categoryNode, categoryModelProjects, overlayDistributions.category[categoryNode.dataset.category].elemId, overlayDistributions.none, thresholds, goiMap, impactOverviewNode, updateImpactOverview);
			}
		});


		//add category sections that are not around yet
		Array.from(this.source.getData()[this.type()].getCategorizedModelProjects().entries())
			.filter(categoryEntry => this.projectsContainer.querySelector('div.tlmd-model-category-info.category_' + categoryEntry[0]) == undefined && categoryEntry[1].length > 0)
			.forEach(categoryEntry => {
				//clone the category template node
				let categoryNode = <HTMLDivElement>this.cloneContainer.querySelector('div.tlmd-model-category-info').cloneNode(true);
				//update node so it reflects the current category and category projects
				this.updateCategoryNode(categoryNode, categoryEntry[1], overlayDistributions.category[categoryEntry[0]].elemId, overlayDistributions.none, thresholds, goiMap, impactOverviewNode, true);

				this.projectsContainer.appendChild(categoryNode);
			})


		//deal with the scalarfield legend content
		if (level <= VisualizationUpdateLevel.data) {
			//all
			if (modelProjects.length > 0) {
				if (!this.impactLegendContainer.firstElementChild) {
					this.impactLegendContainer.appendChild(this.createImpactScoreLegend());
			    }
				
				if (!this.sfAllLegendContainer.firstElementChild) {
					this.sfAllLegendContainer.appendChild(this.createScalarFieldLegend(modelProjects[0], ColorMode.palette));
				}
				
				
			} else {
				this.sfAllLegendContainer.innerHTML = '';
				this.impactLegendContainer.innerHTML = '';
				this.categoryLegendContainer.innerHTML = '';
			}
			//categories
			const categoryLegendEntries = this.sfCategoryLegendContainer.querySelectorAll('div.tlmd-legend-item');
			const categoryLabels: string[] = [];
			const categoryColors: Color[] = [];
			let updateCategoryLegend = false;
			//remove category legends
			categoryLegendEntries.forEach((categoryNode: HTMLDivElement) => {
				if (categoryMap.get(categoryNode.dataset.category) === undefined || categoryMap.get(categoryNode.dataset.category).length === 0) {
					//remove this category because it is not among the set of existing projects
					categoryNode.parentNode.removeChild(categoryNode);
					updateCategoryLegend = true;
				} else {
					const firstCategoryModelProject = categoryMap.get(categoryNode.dataset.category)[0];
					categoryLabels.push(firstCategoryModelProject.parameters.tagLabel);
					categoryColors.push(getCategoryColor(firstCategoryModelProject.parameters.render.hue));
				}
			});
			//add category legends that are not around yet
			Array.from(categoryMap.entries())
				.filter(categoryEntry => this.sfCategoryLegendContainer.querySelector('div.tlmd-legend-item._' + categoryEntry[0]) == undefined && categoryEntry[1].length > 0)
				.forEach(categoryEntry => {
					this.sfCategoryLegendContainer.appendChild(this.createScalarFieldLegend(categoryEntry[1][0], ColorMode.category));
					updateCategoryLegend = true;
					categoryLabels.push(categoryEntry[1][0].parameters.tagLabel);
					categoryColors.push(getCategoryColor(categoryEntry[1][0].parameters.render.hue));
				})
				
				//category legend
				if( updateCategoryLegend){
				replaceNodeContent(this.categoryLegendContainer, categoryLabels.length > 0 ? this.createCategoriesLegend(categoryLabels,categoryColors) : undefined);
				}
		}
		//show category or 'all' scalarfield legends
		if (level <= VisualizationUpdateLevel.settings) {
			if(this.source.getScalarFieldManager(this.mdlType).getModes().colorSchema === ColorMode.category){
				this.sfAllLegendContainer.style.display = 'none';
				this.sfCategoryLegendContainer.style.display = '';
			} else {
				this.sfAllLegendContainer.style.display = '';
				this.sfCategoryLegendContainer.style.display = 'none';
			}
        }
	}

	private setImpacts(projectDetailsContainer: HTMLDivElement, impactsAvaible: boolean, elemId: string, thresholds: number[], goiMap: { [key: string]: GoiDatum }): GoiImpact {

		let impacts = impactsAvaible && this.activeCacheEntry &&
			this.activeCacheEntry.representations &&
			this.activeCacheEntry.representations[elemId]
			? this.activeCacheEntry.representations[elemId].impacts
			: undefined;


		let impactMonitorsNode = <HTMLDivElement>projectDetailsContainer.children[1].children[0];

		let highestMonitor: GoiImpact = undefined;

		if (impactMonitorsNode) {


			Array.from(impactMonitorsNode.children).forEach((impactMonitorNode: HTMLDivElement, index: number) => {
				let goiId = impactMonitorNode.dataset.goi;
				let goiImpacts = impacts ? impacts.gois[goiId] : undefined;

				let impactMonitorSummaryNode = <HTMLDivElement>impactMonitorNode.children[index > 0 ? 0 : 1];
				let impactBandsNode = <HTMLDivElement>impactMonitorNode.children[index > 0 ? 1 : 2].children[0];
				let highestBand: BandImpact = undefined;
				let highestBandChildIndex = -1;

				Array.from(impactBandsNode.children).forEach((impactBandNode: HTMLDivElement, bandIndex: number) => {
					let threshold = thresholds && thresholds.length > bandIndex ? thresholds[bandIndex] : undefined;
					let bandImapct = goiImpacts ? goiImpacts.bands[threshold] : { size: -1, score: { scalar: 0.0, size: 0.0, combined: 0.0 } };
					if (goiImpacts && threshold == goiImpacts.highestBand) {
						highestBandChildIndex = bandIndex;
					}
					if (bandImapct) {
						impactBandNode.children[2].innerHTML = bandImapct.score.combined > 0.0 ? (bandImapct.score.combined * 10).toFixed(0) : '&nbsp;';
						(<HTMLSpanElement>impactBandNode.children[2]).style.backgroundColor = bandImapct.score.combined > 0.0 ? colorToString(getImpactScoreColor( bandImapct.score.combined)) : 'transparent';
						impactBandNode.children[3].innerHTML = (bandImapct.score.size * 100).toFixed(0);
					} else {
						impactBandNode.children[2].innerHTML = '-';
						impactBandNode.children[3].innerHTML = '-';
						(<HTMLSpanElement>impactBandNode.children[2]).style.backgroundColor = 'transparent';
					}
				});
				if (goiImpacts && goiImpacts.highestBand) {
					highestBand = goiImpacts.bands[goiImpacts.highestBand];
					impactMonitorSummaryNode.children[1].innerHTML = impactBandsNode.children[highestBandChildIndex].children[1].innerHTML;
					impactMonitorSummaryNode.children[2].innerHTML = highestBand.score.combined > 0.0 ? (highestBand.score.combined * 10).toFixed(0) : '&nbsp;';
					(<HTMLSpanElement>impactMonitorSummaryNode.children[2]).style.backgroundColor = highestBand.score.combined > 0.0 ? colorToString(getImpactScoreColor(highestBand.score.combined)) : 'transparent';
					impactMonitorSummaryNode.children[3].innerHTML = (highestBand.score.size * 100).toFixed(0);
				} else {
					impactMonitorSummaryNode.children[1].innerHTML = '-';
					impactMonitorSummaryNode.children[2].innerHTML = '-';
					impactMonitorSummaryNode.children[3].innerHTML = '-';
					(<HTMLSpanElement>impactMonitorSummaryNode.children[2]).style.backgroundColor = 'transparent';

				}
			});

			if (impactsAvaible) {
				projectDetailsContainer.classList.remove('no-foldout');
				(<HTMLSpanElement>projectDetailsContainer.children[0].children[0].children[3]).style.visibility = '';
				if (impacts && impacts.highestGoi) {
					highestMonitor = impacts.gois[impacts.highestGoi];
					let highestMonitorBand = highestMonitor.bands[highestMonitor.highestBand];
					projectDetailsContainer.children[0].children[0].children[3].innerHTML = highestMonitorBand.score.combined * 10 > 0.0 ? (highestMonitorBand.score.combined * 10).toFixed(0) : '&nbsp;';
					(<HTMLSpanElement>projectDetailsContainer.children[0].children[0].children[3]).style.backgroundColor = highestMonitorBand.score.combined > 0.0 ? colorToString(getImpactScoreColor(highestMonitorBand.score.combined)) : 'transparent';
				} else {
					projectDetailsContainer.children[0].children[0].children[3].innerHTML = '-';
					(<HTMLSpanElement>projectDetailsContainer.children[0].children[0].children[3]).style.backgroundColor = 'transparent';
				}
			} else {
				projectDetailsContainer.children[0].children[0].children[3].innerHTML = '&nbsp;';
				(<HTMLSpanElement>projectDetailsContainer.children[0].children[0].children[3]).style.backgroundColor = 'transparent';
				(<HTMLSpanElement>projectDetailsContainer.children[0].children[0].children[3]).style.visibility = 'hidden';
				projectDetailsContainer.classList.add('no-foldout');
			}
		}
		return highestMonitor;
	}

	private updateCategoryNode(categoryNode: HTMLDivElement, modelProjects: ModelProject[], overlayId: string, noneDistribution: { [key: string]: OverlayDistribution }, thresholds: number[], goiMap: { [key: string]: GoiDatum }, impactOverviewCopyNode: HTMLDivElement, updateImpactOverview: boolean): HTMLDivElement {
		//add category id to class list
		categoryNode.classList.add('category_' + modelProjects[0].parameters.tag);
		//		//add category id to data set
		categoryNode.dataset.category = modelProjects[0].parameters.tag;

		let categoryDetailNode = <HTMLDivElement>categoryNode.querySelector('div.tlmd-foldout-container.tlmd-project-details.category');
		//add category id to data set
		categoryDetailNode.dataset.category = modelProjects[0].parameters.tag;
		categoryDetailNode.dataset.overlay = overlayId;
		//set the label
		categoryDetailNode.querySelector('span.description').innerHTML = modelProjects[0].parameters.tagLabel;
		//set the background color to the hue specified in the project parameters
		let barBaseColor = getPaletteColor(0, modelProjects[0].parameters.render.hue);
		categoryDetailNode.style.backgroundColor = colorToString(adjustColor(barBaseColor, { hue: 0, saturation: 0, lightness: 0, alpha: -0.5 }));


		if (updateImpactOverview) {
			let impactOverviewNode = impactOverviewCopyNode ? <HTMLDivElement>impactOverviewCopyNode.cloneNode(true) : undefined;
			if (impactOverviewNode) {
				impactOverviewNode.className = 'category_' + modelProjects[0].parameters.tag;
				//console.log('updating category impact overview ' + impactOverviewNode.className);
			}
			replaceNodeContent(categoryDetailNode.querySelector('div.tlmd-foldout'), impactOverviewNode);
		}

		this.setImpacts(categoryDetailNode, this.source.getScalarFieldManager(this.mdlType).getModes().layout === CombineMode.category, overlayId, thresholds, goiMap);

		//update the model projects
		let categoryProjectsNode = <HTMLDivElement>categoryNode.querySelector('div.tlmd-category-projects');

		//update/remove project sections
		Array.from(categoryProjectsNode.children).forEach((projectNode: HTMLDivElement) => {
			let modelProjectIndex = modelProjects.findIndex(modelProject => modelProject.id === projectNode.dataset.project);
			if (modelProjectIndex === -1) {
				//remove this project because it is not among the set of existing projects
				projectNode.parentNode.removeChild(projectNode);
			} else {
				//update project
				let modelProject = modelProjects[modelProjectIndex];
				this.updateProjectNode(projectNode, modelProject, noneDistribution[modelProject.id].elemId, thresholds, goiMap, impactOverviewCopyNode, updateImpactOverview);
			}
		});

		//add model projects sections that are not around yet
		modelProjects
			.filter(modelProject => categoryProjectsNode.querySelector('div.tlmd-project-details.project.project_' + modelProject.id) == undefined)
			.forEach(modelProject => {
				//clone the project template node
				let projectNode = <HTMLDivElement>this.cloneContainer.querySelector('div.tlmd-project-details.project').cloneNode(true);
				//update node so it reflects the current category and category projects
				this.updateProjectNode(projectNode, modelProject, noneDistribution[modelProject.id].elemId, thresholds, goiMap, impactOverviewCopyNode, true);

				categoryProjectsNode.appendChild(projectNode);
			})


		return categoryNode;
	}

	private updateProjectNode(projectNode: HTMLDivElement, modelProject: ModelProject, overlayId: string, thresholds: number[], goiMap: { [key: string]: GoiDatum }, impactOverviewCopyNode: HTMLDivElement, updateImpactOverview: boolean): HTMLDivElement {
		//add project id to class list
		projectNode.classList.add('project_' + modelProject.id);
		//add project id to data set
		projectNode.dataset.project = modelProject.id;
		projectNode.dataset.overlay = overlayId;


		projectNode.querySelector('span.description').innerHTML = modelProject.parameters.description;
		projectNode.querySelector('span.source').innerHTML = modelProject.profile.length > 0 ? modelProject.profile[0].geometry.label : '-';

		if (updateImpactOverview) {
			let impactOverviewNode = impactOverviewCopyNode ? <HTMLDivElement>impactOverviewCopyNode.cloneNode(true) : undefined;
			if (impactOverviewNode) {
				impactOverviewNode.className = 'project_' + modelProject.id;
				//console.log('updating project impact overview ' + impactOverviewNode.className);
			}
			replaceNodeContent(projectNode.querySelector('div.tlmd-foldout'), impactOverviewNode);
		}

		this.setImpacts(projectNode, this.source.getScalarFieldManager(this.mdlType).getModes().layout === CombineMode.none, overlayId, thresholds, goiMap);

		return projectNode;
	}


	private getImpactHash(thresholds: number[], goiList: GoiDatum[]): string {
		return thresholds && goiList && thresholds.length > 0 && goiList.length > 0
			? CryptoJS.MD5(
				goiList.reduce((hash, goi) => hash + '_' + goi, '') +
				'::' +
				thresholds.reduce((hash, threshold) => hash + '_' + threshold.toFixed(1), '')
			).toString(CryptoJS.enc.Hex)
			: undefined;
	}

	private createImpactIndexLookup(thresholds: number[]): { [key: number]: number } {
		return thresholds.reduce((lookup, threshold, index) => {
			lookup[threshold] = index;
			return lookup;
		}, <{ [key: number]: number }>{});
	}

	private createImpactOverview(thresholds: number[], overlayId: string, goiList: GoiDatum[]): HTMLDivElement {

		let impactHash = this.getImpactHash(thresholds, goiList);

		let impactsNode = document.createElement('div');
		impactsNode.classList.add(overlayId);

		if (impactHash) {
			impactsNode.dataset.hash = this.getImpactHash(thresholds, goiList);
			let goiImpactSkeletonNode = <HTMLDivElement>this.cloneContainer.querySelector('div.tlmd-goi-impact').cloneNode(true);
			let bandImpactSkeletonNode = goiImpactSkeletonNode.querySelector('div.tlmd-goi-impact-bands>div>div.tlmd-goi-impact-band');

			goiList.forEach((goi, index) => {
				//clone skeleton
				let goiImpactNode = <HTMLDivElement>goiImpactSkeletonNode.cloneNode(true);
				goiImpactNode.dataset.goi = goi.id;
				goiImpactNode.children[1].children[0].innerHTML = goi.name;
				goiImpactNode.children[1].children[1].innerHTML = '&nbsp;';
				goiImpactNode.children[1].children[2].innerHTML = '&nbsp;';
				goiImpactNode.children[1].children[3].innerHTML = '&nbsp;';

				let goiBandImpactContainerNode = <HTMLDivElement>goiImpactNode.children[2].children[0];
				replaceNodeContent(goiBandImpactContainerNode, undefined); //removes content
				//for each band add a goiBandImpactNode
				thresholds.forEach((threshold, index, list) => {
					let bandImpactNode = <HTMLDivElement>bandImpactSkeletonNode.cloneNode(true);
					(<HTMLSpanElement>bandImpactNode.children[1]).innerHTML = index < list.length - 1 ? threshold.toFixed(1) + ' - ' + list[index + 1].toFixed(1) : '> ' + threshold.toFixed(1);
					(<HTMLSpanElement>bandImpactNode.children[2]).innerHTML = '&nbsp;';
					(<HTMLSpanElement>bandImpactNode.children[3]).innerHTML = '&nbsp;';
					goiBandImpactContainerNode.appendChild(bandImpactNode);
				});
				//only the first goi will show the headers
				if (index > 0) {
					goiImpactNode.removeChild(goiImpactNode.children[0]);
				}
				impactsNode.appendChild(goiImpactNode);
			})
		}
		return impactsNode;
	}

	private createScalarFieldLegend(modelProject: ModelProject, colorMode: ColorMode): HTMLElement {
		const resultUnit = this.inspectionContainer.getDefinition().grid.unitLabel;
		const title = colorMode === ColorMode.category ? `${modelProject.parameters.tagLabel} (${resultUnit})` : resultUnit;
		//TODO render settings should come from modelProjecctType definition. Only the hue should come from the project
		const legend = getScalarFieldLegend(title, modelProject.parameters.render.palette, modelProject.parameters.render.thresholds, colorMode === ColorMode.category ? modelProject.parameters.render.hue : undefined);
		return htl.html`<div class="tlmd-legend-item _${modelProject.parameters.tag}" data-category="${colorMode === ColorMode.category ? modelProject.parameters.tag : "all"}">
		<div clas="tlmd-legend-item-graphic">${legend}</div>
		</div>`;
	}
	
	private createImpactScoreLegend(): HTMLElement {
		const title = this.impactLegendContainer.dataset.label
		const legend = getImpactScoreLegend(title);
		return htl.html`<div class="tlmd-legend-item">
		<div clas="tlmd-legend-item-graphic">${legend}</div>
		</div>`;
	}
	
	private createCategoriesLegend(categoryLabels: string[], categoryColors: Color[] ): HTMLElement {
		const legend = getCategoriesLegend(categoryLabels,categoryColors);
		return htl.html`<div class="tlmd-legend-item">
		<div clas="tlmd-legend-item-graphic">${legend}</div>
		</div>`;
	}

	public release(): void {
		this.locationTimeMonitor.release();
		this.scalarsSubscription.unsubscribe();
	}

	public setMap(map: Map): void {
		if (map) {
			if (this.map) {
				//we already have a map
				if (this.map.getId() !== map.getId()) {
					//we already have another  map
					this.locationTimeMonitor.release();
					this.map = map;
					this.locationTimeMonitor.monitor(this.map, this.timeline, this.handleLocationTimeUpdate);
				} else {
					// the given map is already set
				}
			} else {
				//we did not have a map before
				this.map = map;
				this.locationTimeMonitor.monitor(this.map, this.timeline, this.handleLocationTimeUpdate);
			}
		} else {
			if (this.map) {
				//we have a map that needs to be removed
				this.locationTimeMonitor.release();
				this.map = undefined;
			}
		}
	}
	public setTimeline(timeline: Timeline): void {
		if (timeline) {
			if (this.timeline) {
				//we already have a timeline
				if (this.timeline.getId() !== timeline.getId()) {
					//we already have another  timeline
					this.locationTimeMonitor.release();
					this.timeline = timeline;
					this.locationTimeMonitor.monitor(this.map, this.timeline, this.handleLocationTimeUpdate);
				} else {
					// the given timeline is already set
				}
			} else {
				//we did not have a timeline before
				this.timeline = timeline;
				this.locationTimeMonitor.monitor(this.map, this.timeline, this.handleLocationTimeUpdate);
			}
		} else {
			if (this.timeline) {
				//we have a timeline that needs to be removed
				this.locationTimeMonitor.release();
				this.timeline = undefined;
			}
		}
	}

	public key(): string {
		return this.visualizerKey + '_' + this.type();
	}

	public type(): string {
		return this.mdlType;
	}

	public setControls(_controls: { [key: string]: VisualizerControl }): Visualizer {
		//no controls here
		return this;
	}
}
